import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatApiErrorDetail } from '../utils/api';
import { Heart, Mail, Lock, User as UserIcon, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(name, email, password);
      const firstName = name.trim().split(/\s+/)[0] || 'friend';
      toast.success(`Welcome, ${firstName} — we're so glad you're here.`, {
        description: 'A short welcome note is on its way to your inbox.',
        duration: 6000,
      });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#FDFBF7' }}>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-8">
            <div className="p-2 rounded-xl" style={{ backgroundColor: '#E57A44' }}>
              <Heart className="w-6 h-6" style={{ color: '#FDFBF7', strokeWidth: 2 }} />
            </div>
            <span className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Love Restored</span>
          </Link>

          <h2 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Create Account</h2>
          <p className="mb-8" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Join our community of relationship growth</p>

          {error && (
            <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5' }} data-testid="register-error">
              <p style={{ fontFamily: 'Nunito, sans-serif', color: '#991B1B', fontSize: '14px' }}>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontWeight: 600, fontSize: '14px' }}>Name</label>
              <div className="relative">
                <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#595553', strokeWidth: 2 }} />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full pl-12 pr-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2"
                  style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FFFFFF', borderColor: '#E6E2D8', color: '#2C2A29' }}
                  placeholder="Your name"
                  data-testid="register-name"
                />
              </div>
            </div>

            <div>
              <label className="block mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontWeight: 600, fontSize: '14px' }}>Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#595553', strokeWidth: 2 }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-12 pr-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2"
                  style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FFFFFF', borderColor: '#E6E2D8', color: '#2C2A29' }}
                  placeholder="your@email.com"
                  data-testid="register-email"
                />
              </div>
            </div>

            <div>
              <label className="block mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontWeight: 600, fontSize: '14px' }}>Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#595553', strokeWidth: 2 }} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-12 pr-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2"
                  style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FFFFFF', borderColor: '#E6E2D8', color: '#2C2A29' }}
                  placeholder="••••••••"
                  data-testid="register-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-full transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
              data-testid="register-submit"
            >
              <span>{loading ? 'Creating account...' : 'Create Account'}</span>
              {!loading && <ArrowRight className="w-5 h-5" style={{ strokeWidth: 2 }} />}
            </button>
          </form>

          <p className="mt-8 text-center" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
            Already have an account?{' '}
            <Link to="/login" state={location.state} className="font-semibold transition-colors duration-300" style={{ color: '#E57A44' }} data-testid="register-login-link">
              Login
            </Link>
          </p>

          <p className="mt-12 text-center" data-testid="footer-attribution" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D', fontSize: '13px' }}>
            Created with love by <span style={{ color: '#E57A44', fontWeight: 600 }}>Genesis Natera</span>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#78A18E' }}>
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/297997/pexels-photo-297997.jpeg"
            alt="Couple"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to left, rgba(120, 161, 142, 0.5), rgba(232, 194, 101, 0.3))' }}></div>
        </div>
        <div className="relative z-10 p-12 flex flex-col justify-center">
          <h3 className="text-4xl font-semibold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#FDFBF7' }}>Transform Your Relationships</h3>
          <p className="text-lg" style={{ fontFamily: 'Nunito, sans-serif', color: '#FDFBF7' }}>Get personalized guidance, save your favorite articles, and connect with a supportive community.</p>
        </div>
      </div>
    </div>
  );
}
